<script setup lang="ts">
/**
 * The loop, left to right: three mint stages that build, three cyan stages that serve. A light travels it.
 * Each fact names only what is built: documents are read from Markdown and plain text (W9-DS-02), and a
 * run writes its own examples from the dataset, with no set to review before it (W9-TR-01).
 */
import Icon from '~/components/ui/Icon.vue'
import type { IconName } from '~/utils/icons'

const STAGES: { label: string; icon: IconName; half: 'build' | 'serve'; fact: string }[] = [
  { label: 'Documents', icon: 'data', half: 'build', fact: 'markdown · plain text' },
  { label: 'Dataset', icon: 'datasets', half: 'build', fact: 'questions and answers written from them' },
  { label: 'Train', icon: 'zap', half: 'build', fact: 'LoRA on open weights' },
  { label: 'Compare', icon: 'compare', half: 'serve', fact: 'held-out, base vs yours' },
  { label: 'Serve', icon: 'deploy', half: 'serve', fact: 'OpenAI-compatible endpoint' },
  { label: 'Meter', icon: 'gauge', half: 'serve', fact: 'every minute on the ledger' },
]
</script>

<template>
  <section class="border-b border-border">
    <div class="mx-auto max-w-[var(--measure-1120px)] px-6 py-12">
      <div class="flex items-baseline gap-3">
        <h2 class="m-0 text-[length:var(--type-control)] font-semibold">The loop</h2>
        <span class="num text-[length:var(--type-meta)] text-muted-foreground">build on the left · serve on the right · repeat</span>
      </div>
      <div class="relative mt-8">
        <div class="absolute left-[8%] right-[8%] top-[var(--measure-22px)] hidden h-px overflow-hidden bg-gradient-primary opacity-40 md:block">
          <span class="absolute top-0 h-px w-[12%] animate-travel bg-sweep" />
        </div>
        <div class="grid grid-cols-3 gap-y-8 md:grid-cols-6">
          <div v-for="s in STAGES" :key="s.label" class="relative flex flex-col items-center gap-2 text-center">
            <span
class="grid size-11 place-items-center rounded-[var(--measure-10px)] border bg-card"
                  :class="s.half === 'build' ? 'border-primary/30 text-primary shadow-[0_0_20px_-8px_hsl(var(--primary)/0.8)]' : 'border-info/30 text-info shadow-[0_0_20px_-8px_hsl(var(--info)/0.8)]'">
              <Icon :name="s.icon" :size="17" />
            </span>
            <span class="text-xs font-semibold">{{ s.label }}</span>
            <span class="num max-w-[var(--measure-130px)] text-[length:var(--type-fine)] leading-[1.4] text-muted-foreground">{{ s.fact }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
